import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle, ExternalLink, FileText, Mail, Plus, Shield, Trash2, Upload, Download, Pencil, XCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import apiService from '../../services/api';
import {
  ComplianceReviewStatus,
  CreateVendorInsurancePolicyForm,
  UpdateVendorInsurancePolicyForm,
  VendorCompliance,
  VendorComplianceDocumentType,
  VendorComplianceStatus,
  VendorInsurancePolicy,
} from '../../types';
import {
  formatCurrency,
  formatDate,
  getComplianceStatusClasses,
  getPolicyStatusClasses,
  getReviewStatusClasses,
  normalizeComplianceStatus,
  normalizeReviewStatus,
} from '../../utils/vendorCompliance';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import ConfirmDialog from '../modals/ConfirmDialog';
import InsurancePolicyModal from './InsurancePolicyModal';
import ComplianceReviewModal from './ComplianceReviewModal';
import ComplianceRequirementsChecklist from './ComplianceRequirementsChecklist';

interface VendorCompliancePanelProps {
  vendorId: number;
}

const VendorCompliancePanel: React.FC<VendorCompliancePanelProps> = ({ vendorId }) => {
  const queryClient = useQueryClient();
  const w9InputRef = useRef<HTMLInputElement>(null);
  const policyDocInputRef = useRef<HTMLInputElement>(null);

  const [isPolicyModalOpen, setIsPolicyModalOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<VendorInsurancePolicy | null>(null);
  const [policyToDelete, setPolicyToDelete] = useState<VendorInsurancePolicy | null>(null);
  const [policyForUpload, setPolicyForUpload] = useState<VendorInsurancePolicy | null>(null);
  const [showDeleteW9Confirm, setShowDeleteW9Confirm] = useState(false);
  const [reviewTarget, setReviewTarget] = useState<'w9' | 'code-of-conduct' | 'safety-attestation' | null>(null);
  const codeOfConductInputRef = useRef<HTMLInputElement>(null);
  const safetyAttestationInputRef = useRef<HTMLInputElement>(null);

  const {
    data: complianceResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['vendor-compliance', vendorId],
    queryFn: () => apiService.getVendorCompliance(vendorId),
    enabled: !!vendorId,
  });

  const compliance = complianceResponse?.data;

  const { data: complianceSettingsResponse } = useQuery({
    queryKey: ['company-compliance-settings'],
    queryFn: () => apiService.getCompanyComplianceSettings(),
  });

  const requirePolicyVerification = complianceSettingsResponse?.data?.requirePolicyVerification ?? false;

  const invalidateCompliance = () => {
    queryClient.invalidateQueries({ queryKey: ['vendor-compliance', vendorId] });
    queryClient.invalidateQueries({ queryKey: ['vendor', vendorId] });
    queryClient.invalidateQueries({ queryKey: ['vendors'] });
    queryClient.invalidateQueries({ queryKey: ['company-compliance-summary'] });
    queryClient.invalidateQueries({ queryKey: ['company-compliance-needs-attention'] });
  };
  const createPolicyMutation = useMutation({
    mutationFn: (data: CreateVendorInsurancePolicyForm) =>
      apiService.createVendorInsurancePolicy(vendorId, data),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('Insurance policy added');
        setIsPolicyModalOpen(false);
        setEditingPolicy(null);
        invalidateCompliance();
      } else {
        toast.error(response.message || 'Failed to add policy');
      }
    },
    onError: () => toast.error('Failed to add policy'),
  });

  const updatePolicyMutation = useMutation({
    mutationFn: ({ policyId, data }: { policyId: number; data: UpdateVendorInsurancePolicyForm }) =>
      apiService.updateVendorInsurancePolicy(vendorId, policyId, data),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('Insurance policy updated');
        setIsPolicyModalOpen(false);
        setEditingPolicy(null);
        invalidateCompliance();
      } else {
        toast.error(response.message || 'Failed to update policy');
      }
    },
    onError: () => toast.error('Failed to update policy'),
  });

  const deletePolicyMutation = useMutation({
    mutationFn: (policyId: number) => apiService.deleteVendorInsurancePolicy(vendorId, policyId),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('Insurance policy deleted');
        setPolicyToDelete(null);
        invalidateCompliance();
      } else {
        toast.error(response.message || 'Failed to delete policy');
      }
    },
    onError: () => toast.error('Failed to delete policy'),
  });

  const uploadW9Mutation = useMutation({
    mutationFn: (file: File) => apiService.uploadVendorW9(vendorId, file),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('W-9 uploaded');
        invalidateCompliance();
      } else {
        toast.error(response.message || 'Failed to upload W-9');
      }
    },
    onError: () => toast.error('Failed to upload W-9'),
  });

  const deleteW9Mutation = useMutation({
    mutationFn: () => apiService.deleteVendorW9(vendorId),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('W-9 removed');
        setShowDeleteW9Confirm(false);
        invalidateCompliance();
      } else {
        toast.error(response.message || 'Failed to remove W-9');
      }
    },
    onError: () => toast.error('Failed to remove W-9'),
  });

  const uploadPolicyDocMutation = useMutation({
    mutationFn: ({ policyId, file }: { policyId: number; file: File }) =>
      apiService.uploadVendorInsurancePolicyDocument(vendorId, policyId, file),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('Policy document uploaded');
        setPolicyForUpload(null);
        invalidateCompliance();
      } else {
        toast.error(response.message || 'Failed to upload document');
      }
    },
    onError: () => toast.error('Failed to upload document'),
  });

  const verifyPolicyMutation = useMutation({
    mutationFn: (policyId: number) => apiService.verifyVendorInsurancePolicy(vendorId, policyId),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('Policy verified');
        invalidateCompliance();
      } else {
        toast.error(response.message || 'Failed to verify policy');
      }
    },
    onError: () => toast.error('Failed to verify policy'),
  });

  const sendUploadLinkMutation = useMutation({
    mutationFn: () => apiService.sendVendorComplianceUploadLink(vendorId),
    onSuccess: (response) => {
      if (response.success) {
        toast.success(response.message || 'Upload link sent to vendor');
      } else {
        toast.error(response.message || 'Failed to send upload link');
      }
    },
    onError: () => toast.error('Failed to send upload link'),
  });

  const reviewW9Mutation = useMutation({
    mutationFn: (data: { accept: boolean; rejectionReason?: string }) => apiService.reviewVendorW9(vendorId, data),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('W-9 review updated');
        setReviewTarget(null);
        invalidateCompliance();
      } else {
        toast.error(response.message || 'Failed to review W-9');
      }
    },
    onError: () => toast.error('Failed to review W-9'),
  });

  const uploadComplianceDocMutation = useMutation({
    mutationFn: ({ documentType, file }: { documentType: 'code-of-conduct' | 'safety-attestation'; file: File }) =>
      apiService.uploadVendorComplianceDocument(vendorId, documentType, file),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('Document uploaded');
        invalidateCompliance();
      } else {
        toast.error(response.message || 'Failed to upload document');
      }
    },
    onError: () => toast.error('Failed to upload document'),
  });

  const reviewComplianceDocMutation = useMutation({
    mutationFn: ({
      documentType,
      data,
    }: {
      documentType: 'code-of-conduct' | 'safety-attestation';
      data: { accept: boolean; rejectionReason?: string };
    }) => apiService.reviewVendorComplianceDocument(vendorId, documentType, data),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('Document review updated');
        setReviewTarget(null);
        invalidateCompliance();
      } else {
        toast.error(response.message || 'Failed to review document');
      }
    },
    onError: () => toast.error('Failed to review document'),
  });

  const deleteComplianceDocMutation = useMutation({
    mutationFn: (documentType: 'code-of-conduct' | 'safety-attestation') =>
      apiService.deleteVendorComplianceDocument(vendorId, documentType),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('Document removed');
        invalidateCompliance();
      } else {
        toast.error(response.message || 'Failed to remove document');
      }
    },
    onError: () => toast.error('Failed to remove document'),
  });

  const handlePolicySubmit = (data: CreateVendorInsurancePolicyForm | UpdateVendorInsurancePolicyForm) => {
    if (editingPolicy) {
      updatePolicyMutation.mutate({
        policyId: editingPolicy.id,
        data: data as UpdateVendorInsurancePolicyForm,
      });
      return;
    }

    createPolicyMutation.mutate(data as CreateVendorInsurancePolicyForm);
  };

  const handleDownloadDocument = async (documentPath?: string) => {
    if (!documentPath) return;

    try {
      const blob = await apiService.downloadVendorComplianceDocument(vendorId, documentPath);
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch {
      toast.error('Failed to open document');
    }
  };

  const handleW9FileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      uploadW9Mutation.mutate(file);
    }
    event.target.value = '';
  };

  const handlePolicyDocFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && policyForUpload) {
      uploadPolicyDocMutation.mutate({ policyId: policyForUpload.id, file });
    }
    event.target.value = '';
    setPolicyForUpload(null);
  };

  const handleComplianceDocFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    documentType: 'code-of-conduct' | 'safety-attestation'
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      uploadComplianceDocMutation.mutate({ documentType, file });
    }
    event.target.value = '';
  };

  const getComplianceDocument = (documentType: VendorComplianceDocumentType) =>
    compliance?.documents?.find((doc) => doc.documentType === documentType);

  const handleRejectConfirm = (rejectionReason: string) => {
    if (reviewTarget === 'w9') {
      reviewW9Mutation.mutate({ accept: false, rejectionReason });
      return;
    }

    if (reviewTarget === 'code-of-conduct' || reviewTarget === 'safety-attestation') {
      reviewComplianceDocMutation.mutate({
        documentType: reviewTarget,
        data: { accept: false, rejectionReason },
      });
    }
  };

  const renderDocumentCard = (
    title: string,
    documentType: VendorComplianceDocumentType,
    uploadType: 'code-of-conduct' | 'safety-attestation',
    inputRef: React.RefObject<HTMLInputElement | null>
  ) => {
    const document = getComplianceDocument(documentType);

    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">{title}</CardTitle>
          <div className="flex items-center gap-2">
            {document?.documentPath && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleDownloadDocument(document.documentPath)}
              >
                <Download className="h-4 w-4 mr-1" />
                View
              </Button>
            )}
            {document?.documentPath && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => deleteComplianceDocMutation.mutate(uploadType)}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Remove
              </Button>
            )}
            <Button type="button" size="sm" onClick={() => inputRef.current?.click()}>
              <Upload className="h-4 w-4 mr-1" />
              {document?.documentPath ? 'Replace' : 'Upload'}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-gray-600 dark:text-gray-400">Status</span>
            <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getReviewStatusClasses(document?.reviewStatus)}`}>
              {document?.reviewStatusLabel || 'Not on File'}
            </span>
          </div>
          {document?.rejectionReason && (
            <p className="text-red-600 text-sm">{document.rejectionReason}</p>
          )}
          {normalizeReviewStatus(document?.reviewStatus, document?.reviewStatusLabel) === ComplianceReviewStatus.PendingReview && (
            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                size="sm"
                onClick={() => reviewComplianceDocMutation.mutate({ documentType: uploadType, data: { accept: true } })}
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                Accept
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setReviewTarget(uploadType)}
              >
                <XCircle className="h-4 w-4 mr-1" />
                Reject
              </Button>
            </div>
          )}
          <input
            ref={inputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            className="hidden"
            onChange={(e) => handleComplianceDocFileChange(e, uploadType)}
          />
        </CardContent>
      </Card>
    );
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded-lg" />
        <div className="h-40 bg-gray-200 dark:bg-gray-700 rounded-lg" />
      </div>
    );
  }

  if (error || !compliance) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-red-600">
          Failed to load compliance information.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6 max-w-full">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-4 py-3">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Compliance rules are configured at the company level (required policies, minimum limits, and review settings).
        </p>
        <Link
          to="/admin/vendor-compliance"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-accent-color hover:underline whitespace-nowrap"
        >
          Manage compliance requirements
          <ExternalLink className="h-4 w-4" />
        </Link>
      </div>

      <ComplianceStatusBanner compliance={compliance} />

      {compliance.requirements && compliance.requirements.length > 0 && (
        <ComplianceRequirementsChecklist requirements={compliance.requirements} />
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            W-9 Tax Document
          </CardTitle>
          <div className="flex items-center gap-2">
            {compliance.w9OnFile && compliance.w9DocumentPath && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleDownloadDocument(compliance.w9DocumentPath)}
              >
                <Download className="h-4 w-4 mr-1" />
                View
              </Button>
            )}
            {compliance.w9OnFile ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowDeleteW9Confirm(true)}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Remove
              </Button>
            ) : null}
            <Button
              type="button"
              size="sm"
              onClick={() => w9InputRef.current?.click()}
              disabled={uploadW9Mutation.isPending}
            >
              <Upload className="h-4 w-4 mr-1" />
              {compliance.w9OnFile ? 'Replace W-9' : 'Upload W-9'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">Status</span>
            <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getReviewStatusClasses(compliance.w9ReviewStatus)}`}>
              {compliance.w9ReviewStatusLabel || (compliance.w9OnFile ? 'On File' : 'Not on File')}
            </span>
          </div>
          {compliance.w9RejectionReason && (
            <p className="text-sm text-red-600 mt-2">{compliance.w9RejectionReason}</p>
          )}
          {normalizeReviewStatus(compliance.w9ReviewStatus, compliance.w9ReviewStatusLabel) === ComplianceReviewStatus.PendingReview && (
            <div className="flex gap-2 mt-3">
              <Button
                type="button"
                size="sm"
                onClick={() => reviewW9Mutation.mutate({ accept: true })}
                disabled={reviewW9Mutation.isPending}
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                Accept
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setReviewTarget('w9')}
                disabled={reviewW9Mutation.isPending}
              >
                <XCircle className="h-4 w-4 mr-1" />
                Reject
              </Button>
            </div>
          )}
          {compliance.w9ReceivedDate && (
            <div className="flex items-center justify-between text-sm mt-2">
              <span className="text-gray-600 dark:text-gray-400">Received Date</span>
              <span>{formatDate(compliance.w9ReceivedDate)}</span>
            </div>
          )}
          <input
            ref={w9InputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            className="hidden"
            onChange={handleW9FileChange}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Insurance Policies (COI)
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => sendUploadLinkMutation.mutate()}
              disabled={sendUploadLinkMutation.isPending}
              aria-label="Send COI upload link to vendor"
            >
              <Mail className="h-4 w-4 mr-1" />
              {sendUploadLinkMutation.isPending ? 'Sending...' : 'Send upload link'}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => {
                setEditingPolicy(null);
                setIsPolicyModalOpen(true);
              }}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Policy
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {requirePolicyVerification && (
            <div className="mb-4 flex items-start gap-2 rounded-md border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm text-yellow-900 dark:border-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>
                COI verification is required. Upload each policy certificate, then click <strong>Verify COI</strong> to mark it as reviewed.
              </span>
            </div>
          )}
          {compliance.policies.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No insurance policies on file. Add General Liability, Auto, Umbrella, or Workers Compensation coverage.
            </p>
          ) : (
            <div className="overflow-x-auto max-w-full">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                <thead>
                  <tr className="text-left text-gray-500 dark:text-gray-400">
                    <th className="py-2 pr-4">Type</th>
                    <th className="py-2 pr-4">Carrier</th>
                    <th className="py-2 pr-4">Policy #</th>
                    <th className="py-2 pr-4">Expires</th>
                    <th className="py-2 pr-4">Limit</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4">Verified</th>
                    <th className="py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {compliance.policies.map((policy) => (
                    <tr key={policy.id}>
                      <td className="py-3 pr-4 font-medium text-gray-900 dark:text-gray-100">
                        {policy.policyTypeLabel}
                      </td>
                      <td className="py-3 pr-4">{policy.carrierName || '-'}</td>
                      <td className="py-3 pr-4">{policy.policyNumber || '-'}</td>
                      <td className="py-3 pr-4">{formatDate(policy.expirationDate)}</td>
                      <td className="py-3 pr-4">{formatCurrency(policy.coverageLimit)}</td>
                      <td className="py-3 pr-4">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getPolicyStatusClasses(policy.policyStatus)}`}>
                          {policy.policyStatus}
                        </span>
                      </td>
                      <td className="py-3 pr-4">
                        {policy.isVerified ? (
                          <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Verified
                          </span>
                        ) : policy.documentPath ? (
                          <div className="flex flex-col items-start gap-1.5">
                            {requirePolicyVerification && (
                              <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                Unverified
                              </span>
                            )}
                            <Button
                              type="button"
                              variant={requirePolicyVerification ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => verifyPolicyMutation.mutate(policy.id)}
                              disabled={verifyPolicyMutation.isPending}
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Verify COI
                            </Button>
                          </div>
                        ) : (
                          <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                            No COI uploaded
                          </span>
                        )}
                      </td>
                      <td className="py-3 text-right">
                        <div className="flex justify-end gap-1">
                          {policy.documentPath && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDownloadDocument(policy.documentPath)}
                              title="View document"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setPolicyForUpload(policy);
                              policyDocInputRef.current?.click();
                            }}
                            title="Upload document"
                          >
                            <Upload className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setEditingPolicy(policy);
                              setIsPolicyModalOpen(true);
                            }}
                            title="Edit policy"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => setPolicyToDelete(policy)}
                            title="Delete policy"
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <input
            ref={policyDocInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            className="hidden"
            onChange={handlePolicyDocFileChange}
          />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {renderDocumentCard('Code of Conduct', VendorComplianceDocumentType.CodeOfConduct, 'code-of-conduct', codeOfConductInputRef)}
        {renderDocumentCard('Safety Attestation', VendorComplianceDocumentType.SafetyAttestation, 'safety-attestation', safetyAttestationInputRef)}
      </div>

      <InsurancePolicyModal
        isOpen={isPolicyModalOpen}
        onClose={() => {
          setIsPolicyModalOpen(false);
          setEditingPolicy(null);
        }}
        onSubmit={handlePolicySubmit}
        policy={editingPolicy}
        isLoading={createPolicyMutation.isPending || updatePolicyMutation.isPending}
      />

      <ConfirmDialog
        isOpen={!!policyToDelete}
        onClose={() => setPolicyToDelete(null)}
        onConfirm={() => policyToDelete && deletePolicyMutation.mutate(policyToDelete.id)}
        title="Delete Insurance Policy"
        message={`Delete the ${policyToDelete?.policyTypeLabel} policy? This cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deletePolicyMutation.isPending}
      />

      <ConfirmDialog
        isOpen={showDeleteW9Confirm}
        onClose={() => setShowDeleteW9Confirm(false)}
        onConfirm={() => deleteW9Mutation.mutate()}
        title="Remove W-9"
        message="Remove the W-9 document from this vendor? The vendor will be marked as non-compliant until a new W-9 is uploaded."
        confirmText="Remove W-9"
        variant="warning"
        isLoading={deleteW9Mutation.isPending}
      />

      <ComplianceReviewModal
        isOpen={reviewTarget !== null}
        onClose={() => setReviewTarget(null)}
        onConfirm={handleRejectConfirm}
        title={
          reviewTarget === 'w9'
            ? 'Reject W-9'
            : reviewTarget === 'code-of-conduct'
              ? 'Reject Code of Conduct'
              : 'Reject Safety Attestation'
        }
        isLoading={reviewW9Mutation.isPending || reviewComplianceDocMutation.isPending}
      />
    </div>
  );
};

const ComplianceStatusBanner: React.FC<{ compliance: VendorCompliance }> = ({ compliance }) => {
  const normalizedStatus = normalizeComplianceStatus(
    compliance.complianceStatus,
    compliance.complianceStatusLabel
  );

  const borderClass =
    normalizedStatus === VendorComplianceStatus.Compliant
      ? 'border-green-200'
      : normalizedStatus === VendorComplianceStatus.ExpiringSoon
        ? 'border-yellow-200'
        : 'border-red-200';

  return (
  <Card className={borderClass}>
    <CardContent className="pt-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Shield className="h-5 w-5 text-gray-700 dark:text-gray-300" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Compliance Status</h3>
            <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${getComplianceStatusClasses(compliance.complianceStatus, compliance.complianceStatusLabel)}`}>
              {compliance.complianceStatusLabel}
            </span>
          </div>
          {compliance.issues.length > 0 ? (
            <ul className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
              {compliance.issues.map((issue) => (
                <li key={`${issue.code}-${issue.policyId ?? 'general'}`} className="flex items-start gap-2">
                  <AlertTriangle className={`h-4 w-4 mt-0.5 flex-shrink-0 ${issue.severity === 'Blocking' ? 'text-red-500' : 'text-yellow-500'}`} />
                  <span>{issue.message}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              All required compliance documents are valid.
            </p>
          )}
        </div>
      </div>
    </CardContent>
  </Card>
  );
};

export default VendorCompliancePanel;
